import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Download } from "lucide-react";

const PAYMENT_MODES = ["UPI", "Bank Transfer", "Cash", "Credit Card"];

export default function BulkGenerate({ tenants, defaultMonth, today, toMonthValue, fromMonthValue, bounds, onGenerated }) {
  const [rows, setRows] = useState([]);
  const [issued, setIssued] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState([]);

  useEffect(() => {
    api.get("/receipts").then((r) => {
      const set = new Set(r.data.map((x) => `${x.tenant_id}|${x.month_year}`));
      setIssued(set);
      setRows(tenants.filter((t) => t.active !== false).map((t) => ({
        id: t.id, name: t.full_name, room: t.room_no, amount: t.monthly_rent ? String(t.monthly_rent) : "",
        mode: "UPI", month: defaultMonth, date: today,
        selected: !set.has(`${t.id}|${defaultMonth}`),
      })));
    }).catch(() => toast.error("Could not load tenants for bulk mode"));
  }, [tenants, defaultMonth, today]);

  const upd = (id, k, v) => setRows((rs) => rs.map((r) => {
    if (r.id !== id) return r;
    const n = { ...r, [k]: v };
    if (k === "month" && issued.has(`${id}|${v}`)) n.selected = false;
    return n;
  }));
  const selected = rows.filter((r) => r.selected);
  const allOn = rows.length > 0 && selected.length === rows.length;

  const generateAll = async () => {
    if (!selected.length) return toast.error("Select at least one tenant");
    const bad = selected.find((r) => !(parseFloat(r.amount) > 0) || !r.date || r.date > today);
    if (bad) return toast.error(`Check amount and payment date for ${bad.name}`);
    setBusy(true);
    const ok = [];
    let failed = 0;
    for (const r of selected) {
      try {
        const res = await api.post("/receipts", {
          tenant_id: r.id, month_year: r.month, amount_paid: parseFloat(r.amount),
          payment_mode: r.mode, payment_date: r.date, notes: "",
        });
        ok.push(res.data);
      } catch { failed += 1; }
    }
    setBusy(false);
    setDone(ok);
    if (ok.length) toast.success(`${ok.length} receipt(s) generated`);
    if (failed) toast.error(`${failed} receipt(s) failed — please retry those`);
    onGenerated?.();
  };

  if (done.length) {
    return (
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 space-y-2" data-testid="bulk-result-card">
        <p className="font-semibold text-emerald-700 flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> {done.length} receipts generated</p>
        {done.map((d) => (
          <div key={d.id} className="flex items-center justify-between text-sm bg-white rounded-md px-3 py-2 border border-emerald-100" data-testid={`bulk-result-row-${d.tenant_id}`}>
            <span>{d.receipt_no} · {d.tenant_name} · {d.month_year} · ₹ {Number(d.amount_paid).toLocaleString("en-IN")}</span>
            <Button size="sm" variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${d.pdf_url}`, "_blank")} data-testid={`bulk-result-download-${d.tenant_id}`}>
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
          </div>
        ))}
        <Button className="bg-amber-500 hover:bg-amber-600 text-white mt-2" onClick={() => setDone([])} data-testid="bulk-generate-again-button">Back to bulk list</Button>
      </div>
    );
  }

  return (
    <div data-testid="bulk-generate-panel">
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="p-2 w-8"><Checkbox checked={allOn} onCheckedChange={(v) => setRows((rs) => rs.map((r) => ({ ...r, selected: !!v })))} data-testid="bulk-select-all-checkbox" /></th>
              <th className="p-2 text-left">Tenant</th><th className="p-2 text-left">Rent (₹)</th><th className="p-2 text-left">Mode</th>
              <th className="p-2 text-left">Month</th><th className="p-2 text-left">Payment date</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-slate-500">No active tenants</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`bulk-row-${r.id}`}>
                <td className="p-2"><Checkbox checked={r.selected} onCheckedChange={(v) => upd(r.id, "selected", !!v)} data-testid={`bulk-row-checkbox-${r.id}`} /></td>
                <td className="p-2">
                  <p className="font-medium">{r.name}</p>
                  <p className="text-xs text-slate-500">Room {r.room}</p>
                  {issued.has(`${r.id}|${r.month}`) && (
                    <p className="text-[11px] text-amber-600 flex items-center gap-1" data-testid={`bulk-row-issued-${r.id}`}><AlertTriangle className="h-3 w-3" /> Already issued for {r.month}</p>
                  )}
                </td>
                <td className="p-2"><Input type="number" className="w-28" value={r.amount} onChange={(e) => upd(r.id, "amount", e.target.value)} data-testid={`bulk-row-amount-${r.id}`} /></td>
                <td className="p-2">
                  <Select value={r.mode} onValueChange={(v) => upd(r.id, "mode", v)}>
                    <SelectTrigger className="w-36" data-testid={`bulk-row-mode-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-2">
                  <Input type="month" className="w-40" value={toMonthValue(r.month)} min={bounds.min} max={bounds.max}
                    onChange={(e) => e.target.value && upd(r.id, "month", fromMonthValue(e.target.value))} data-testid={`bulk-row-month-${r.id}`} />
                </td>
                <td className="p-2"><Input type="date" className="w-40" value={r.date} max={today} onChange={(e) => upd(r.id, "date", e.target.value)} data-testid={`bulk-row-date-${r.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end pt-4">
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={generateAll} disabled={busy || !selected.length} data-testid="bulk-generate-all-button">
          {busy ? "Generating…" : `Generate All (${selected.length})`}
        </Button>
      </div>
    </div>
  );
}
